import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ImageSectionHeadComponent } from '../../components/image-section-head/image-section-head.component';
import { ApplicantsService } from '../../services/http/applicants/applicants.service';
import { ApplicantIndividual, ApplicantIndividualContact, ApplicantIndividualIdentification, ApplicantOrganisationIdentification, ApplicantRequest, Relationship } from '../../services/http/applicants/applicants.interface';
import { CodeName, RequestDate } from '../../services/http/common.http.interface';
import { AlertType } from '../../services/alert/alert.interface';
import { AlertService } from '../../services/alert/alert.service';
import { MunicipalitiesService } from '../../services/http/municipalities/municipalities.service';
import { LoaderService } from '../../services/loader/loader.service';
import { Country } from '../../services/http/countries/countries.interface';
import { MunicipalityProvinceData } from '../../services/http/municipalities/municipalities.interface';
import { Province } from '../../services/http/provinces/provinces.interface';
import { CountriesService } from '../../services/http/countries/countries.service';
import { ProvincesService } from '../../services/http/provinces/provinces.service';
import { ExceptionsService } from '../../services/http/exceptions/exceptions.service';
import { VehiclesComponent } from '../../components/vehicles/vehicles.component';
import { ExceptionComponent } from '../../components/exception/exception.component';
import { VehicleComponent } from '../../components/vehicle/vehicle.component';
import { AccessComponent } from '../../components/access/access.component';

@Component({
  selector: 'app-users',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, ImageSectionHeadComponent, VehiclesComponent, ExceptionComponent, VehicleComponent, AccessComponent],
  templateUrl: './users.component.html',
  styleUrl: './users.component.scss'
})
export class UsersComponent {
  title = $localize`:@@Router.Private.Users:Usuarios`;
  description = "";
  image = "assets/images/teclado.jpg";

  dni = "";

  relationships: Relationship[] = [];
  countries: Country[] = [];
  provinces: Province[] = [];
  municipalities: MunicipalityProvinceData[] = [];

  applicant: ApplicantRequest | null = null;
  isCreate = false;
  isValid = false;
  confirmEmail = "";
  password = "";
  password2 = "";
  streetTypes: CodeName[] = [];

  showNew = false;
  notFoundUser = "";

  newException = false;
  selectedVehicle: string | null = null;
  newVehicle = false;

  constructor(
    private readonly applicantService: ApplicantsService,
    private readonly loaderService: LoaderService,
    private readonly alertService: AlertService,
    private readonly municipalitiesService: MunicipalitiesService,
    private readonly countriesService: CountriesService,
    private readonly provincesService: ProvincesService,
    private readonly exceptionsService: ExceptionsService
  ) {
    this.applicantService.getRelationships().subscribe(
      data => {
        this.relationships = data.relationships;
      }
    );
    this.countriesService.getCountries().subscribe(
      data => {
        this.countries = data.countries;
      }
    );
    this.provincesService.getProvinces().subscribe(
      data => {
        this.provinces = data.provinces;
      }
    );
    this.exceptionsService.getSteetTypes().subscribe(
      data => {
        this.streetTypes = data.streets;
      }
    );
  }

  findUser() {
    this.applicant = null;
    this.applicantService.getApplicant(this.dni).subscribe({
      next: (data) => {
        this.applicant = data;
        this.isCreate = false;
        this.showNew = false;
        this.notFoundUser = "";
        this.loadMunicipalities();
      },
      error: (error) => {
        this.isCreate = true;
        this.showNew = true;
        this.notFoundUser = $localize`:@@Users.NotFound:No existe solicitante registrado con este DNI`;
        // this.applicant = {
        //   applicantType: "Individual",
        //   attachments: [],
        //   comments: "",
        //   company: undefined,
        //   identifier: "",
        //   individual: {
        //     applicantIdentification: {
        //       name: "",
        //       firstSurname: "",
        //       secondSurname: "",
        //       dateOfBirth: {
        //         date: "",
        //       },
        //       documentType: "",
        //       documentIdentification: "",
        //     },
        //     applicantContact: {
        //       countryCode: 0,
        //       phoneNumber: "",
        //       province: "",
        //       municipality: "",
        //       streetName: "",
        //       streetNumber: "",
        //       floor: "",
        //       door: "",
        //       stairCase: "",
        //       block: "",
        //       postalCode: "",
        //       email: "",
        //       streetTypeCode: "",
        //       streetTypeName: "",
        //     },
        //   },
        //   password: "",
        //   represented: undefined,
        //   flags: {
        //     email: false,
        //     sms: false,
        //     personal: false,
        //     protection: false,
        //     privacity: false,
        //     veracity: false,
        //     language: "ca-ES"
        //   }
        // }
      }
    });
  }

  createOrUpdate() {
    this.formSubmitted = true; // Mark form as submitted to show all errors
    if (!this.isValid) {
      // Find all required fields that are empty
      const emptyFields: string[] = [];

      switch (this.applicant?.applicantType) {
        case 'Individual':
          // Check individual fields and add to emptyFields if needed
          // ... existing validation logic ...
          break;
        case 'Company':
          // Check company fields and add to emptyFields if needed
          // ... existing validation logic ...
          break;
        case 'Represented':
          // Check represented fields and add to emptyFields if needed
          // ... existing validation logic ...
          break;
      }
      // if not privacy show alert
      if (!this.applicant?.flags!.privacity) {
        this.alertService.setAlert({
          message: $localize`:@@Users.Privacy:Debe aceptar la política de privacidad`,
          type: AlertType.Error,
          dismissible: true,
          dismissOnTimeout: 5000
        });
        return;
      }
      this.alertService.setAlert({
        message: $localize`:@@Users.Required:Rellene todos los campos requeridos`,
        type: AlertType.Error,
        dismissible: true,
        dismissOnTimeout: 5000
      });
      return;
    }
    this.loaderService.show();
    let validEmail = false;
    let emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    switch (this.applicant?.applicantType) {
      case 'Individual':
        if (this.applicant.individual && this.applicant.individual.applicantContact?.email.match(emailRegex))
          validEmail = this.confirmEmail === this.applicant.individual.applicantContact.email;
        break;
      case 'Company':
        if (this.applicant.company && this.applicant.company.applicantContact.email.match(emailRegex))
          validEmail = this.confirmEmail === this.applicant.company.applicantContact.email;
        break;
      case 'Represented':
        if (this.applicant.represented && this.applicant.represented.applicantContact.email.match(emailRegex))
          validEmail = this.confirmEmail === this.applicant.represented.applicantContact.email;
        break;
    }

    if (validEmail) {
      this.applicant!.flags!.email = true;
      this.applicant!.flags!.sms = true;
      if (this.password !== "")
        this.applicant!.password = this.password;

      // Save
      if (this.isCreate) {
        this.applicantService.updateApplicant(this.applicant!).subscribe({
          next: (data) => {
            this.loaderService.hide();
            this.applicant = null;
            this.alertService.setAlert({
              message: $localize`:@@Users.Updated:Usuario actualizado correctamente`,
              type: AlertType.Success,
              dismissible: true,
              dismissOnTimeout: 2000
            });
          }
        });
      } else {
        this.applicantService.createApplicant(this.applicant!).subscribe({
          next: (data) => {
            this.loaderService.hide();
            this.applicant = null;
            this.alertService.setAlert({
              message: $localize`:@@Users.Created:Usuario creado correctamente`,
              type: AlertType.Success,
              dismissible: true,
              dismissOnTimeout: 2000
            });
          }
        });
      }
    } else {
      this.loaderService.hide();
      this.alertService.setAlert({
        message: $localize`:@@Users.EmailError:El email es incorrecto`,
        type: AlertType.Error,
        dismissible: true,
        dismissOnTimeout: 2000
      });
    }
  }

  checkValid() {
    this.isValid = false;
    switch (this.applicant?.applicantType) {
      case 'Individual':
        this.applicant.identifier = this.applicant.individual!.applicantIdentification.documentIdentification;
        this.isValid =
          this.applicant.individual?.applicantIdentification.name !== "" &&
          this.applicant.individual?.applicantIdentification.firstSurname !== "" &&
          this.applicant.individual?.applicantIdentification.dateOfBirth.date !== "" &&
          this.applicant.individual?.applicantIdentification.documentType !== "" &&
          this.applicant.individual?.applicantIdentification.documentIdentification !== "" &&
          this.applicant.individual?.applicantContact?.email !== "" &&
          (this.confirmEmail !== "" || !this.isCreate) &&
          // (this.password !== "" || !this.isCreate) &&
          // (this.password2 !== "" || !this.isCreate) &&
          // this.password === this.password2 &&
          this.applicant.flags!.privacity;
        break;
      case 'Company':
        this.applicant.identifier = this.applicant.company!.applicantIdentification.documentIdentification;
        this.isValid =
          this.applicant.company?.applicantIdentification.name !== "" &&
          this.applicant.company?.applicantIdentification.firstSurname !== "" &&
          this.applicant.company?.applicantIdentification.dateOfBirth.date !== "" &&
          this.applicant.company?.applicantIdentification.documentType !== "" &&
          this.applicant.company?.applicantIdentification.documentIdentification !== "" &&
          this.applicant.company?.applicantContact.email !== "" &&
          (this.confirmEmail !== "" || !this.isCreate) &&

          this.applicant.company?.identification.documentIdentification !== "" &&
          this.applicant.company?.identification.documentType !== "" &&
          this.applicant.company?.identification.registeredName !== "" &&

          this.applicant.company?.relationship !== "" &&
          this.applicant.company?.representativeIdentification.name !== "" &&
          this.applicant.company?.representativeIdentification.firstSurname !== "" &&
          this.applicant.company?.representativeIdentification.dateOfBirth.date !== "" &&
          this.applicant.company?.representativeIdentification.documentType !== "" &&
          this.applicant.company?.representativeIdentification.documentIdentification !== "" &&

          // (this.password !== "" || !this.isCreate) &&
          // (this.password2 !== "" || !this.isCreate) &&
          // this.password === this.password2 &&

          this.applicant.flags!.privacity;
        break;
      case 'Represented':
        this.applicant.identifier = this.applicant.represented!.applicantIdentification.documentIdentification;
        this.isValid =
          this.applicant.represented?.applicantIdentification.name !== "" &&
          this.applicant.represented?.applicantIdentification.firstSurname !== "" &&
          this.applicant.represented?.applicantIdentification.dateOfBirth.date !== "" &&
          this.applicant.represented?.applicantIdentification.documentType !== "" &&
          this.applicant.represented?.applicantIdentification.documentIdentification !== "" &&
          this.applicant.represented?.applicantContact.email !== "" &&
          (this.confirmEmail !== "" || !this.isCreate) &&

          this.applicant.represented?.relationship !== "" &&
          this.applicant.represented?.representativeIdentification.name !== "" &&
          this.applicant.represented?.representativeIdentification.firstSurname !== "" &&
          this.applicant.represented?.representativeIdentification.dateOfBirth.date !== "" &&
          this.applicant.represented?.representativeIdentification.documentType !== "" &&
          this.applicant.represented?.representativeIdentification.documentIdentification !== "" &&

          // (this.password !== "" || !this.isCreate) &&
          // (this.password2 !== "" || !this.isCreate) &&
          // this.password === this.password2 &&

          this.applicant.flags!.privacity;
        break;
    }
  }

  clearOtherForms() {
    this.isValid = false;
    switch (this.applicant?.applicantType) {
      case 'Individual':
        this.applicant.individual = {
          applicantIdentification: {
            name: "",
            firstSurname: "",
            secondSurname: "",
            dateOfBirth: {
              date: ""
            } as RequestDate,
            documentType: "NIF",
            documentIdentification: this.dni
          } as ApplicantIndividualIdentification,
          applicantContact: {
            countryCode: 0,
            phoneNumber: "",
            province: "",
            municipality: "",
            streetName: "",
            streetNumber: "",
            floor: "",
            door: "",
            stairCase: "",
            block: "",
            postalCode: "",
            email: ""
          } as ApplicantIndividualContact
        } as ApplicantIndividual;
        this.applicant.company = undefined;
        this.applicant.represented = undefined;
        break;
      case 'Company':
        this.applicant.individual = undefined;
        this.applicant.company = {
          applicantIdentification: {
            name: "",
            firstSurname: "",
            secondSurname: "",
            dateOfBirth: {
              date: ""
            } as RequestDate,
            documentType: "NIF",
            documentIdentification: this.dni
          } as ApplicantIndividualIdentification,
          applicantContact: {
            countryCode: 0,
            phoneNumber: "",
            province: "",
            municipality: "",
            streetName: "",
            streetNumber: "",
            floor: "",
            door: "",
            stairCase: "",
            block: "",
            postalCode: "",
            email: ""
          } as ApplicantIndividualContact,
          identification: {
            documentIdentification: "",
            documentType: "",
            registeredName: ""
          } as ApplicantOrganisationIdentification,
          relationship: "",
          representativeContact: {
            countryCode: 0,
            phoneNumber: "",
            province: "",
            municipality: "",
            streetName: "",
            streetNumber: "",
            floor: "",
            door: "",
            stairCase: "",
            block: "",
            postalCode: "",
            email: "",
          } as ApplicantIndividualContact,
          representativeIdentification: {
            name: "",
            firstSurname: "",
            secondSurname: "",
            dateOfBirth: {
              date: ""
            } as RequestDate,
            documentType: "",
            documentIdentification: ""
          } as ApplicantIndividualIdentification
        };
        this.applicant.represented = undefined;
        break;
      case 'Represented':
        this.applicant.individual = undefined;
        this.applicant.company = undefined;
        this.applicant.represented = {
          applicantContact: {
            countryCode: 0,
            phoneNumber: "",
            province: "",
            municipality: "",
            streetName: "",
            streetNumber: "",
            floor: "",
            door: "",
            stairCase: "",
            block: "",
            postalCode: "",
            email: ""
          } as ApplicantIndividualContact,
          applicantIdentification: {
            name: "",
            firstSurname: "",
            secondSurname: "",
            dateOfBirth: {
              date: ""
            } as RequestDate,
            documentType: "NIF",
            documentIdentification: this.dni
          } as ApplicantIndividualIdentification,
          relationship: "",
          representativeContact: {
            countryCode: 0,
            phoneNumber: "",
            province: "",
            municipality: "",
            streetName: "",
            streetNumber: "",
            floor: "",
            door: "",
            stairCase: "",
            block: "",
            postalCode: "",
            email: ""
          } as ApplicantIndividualContact,
          representativeIdentification: {
            name: "",
            firstSurname: "",
            secondSurname: "",
            dateOfBirth: {
              date: ""
            } as RequestDate,
            documentType: "",
            documentIdentification: ""
          } as ApplicantIndividualIdentification
        };
        break;
    }
  }

  loadMunicipalities() {
    let province = '';
    switch (this.applicant?.applicantType) {
      case 'Individual':
        province = this.applicant.individual!.applicantContact?.province || '';
        break;
      case 'Company':
        province = this.applicant.company!.applicantContact.province;
        break;
      case 'Represented':
        province = this.applicant.represented!.applicantContact.province;
        break;
    }
    this.municipalitiesService.getMunicipalitiesByProvince(province).subscribe(
      data => {
        this.municipalities = data.municipalities;
      }
    );
  }

  // on key press enter filter
  onKeyPress(event: any) {
    if (event.keyCode === 13) {
      this.findUser();
    }
  }

  showCreateUser() {
    this.applicant = {
      applicantType: "Individual",
      attachments: [],
      comments: "",
      company: undefined,
      identifier: "",
      individual: {
        applicantIdentification: {
          name: "",
          firstSurname: "",
          secondSurname: "",
          dateOfBirth: {
            date: "",
          },
          documentType: "",
          documentIdentification: "",
        },
        applicantContact: {
          countryCode: 0,
          phoneNumber: "",
          province: "",
          municipality: "",
          streetName: "",
          streetNumber: "",
          floor: "",
          door: "",
          stairCase: "",
          block: "",
          postalCode: "",
          email: "",
          streetTypeCode: "",
          streetTypeName: "",
        },
      },
      //password: "weoirj66uHT.",
      represented: undefined,
      flags: {
        email: false,
        sms: false,
        personal: false,
        protection: false,
        privacity: false,
        veracity: false,
        language: "ca-ES",
        vehicleDocumentation: false
      }
    }
    this.isCreate = true;
    this.showNew = true;
    this.notFoundUser = "";
  }

  goBackVehicles() {
    this.newVehicle = false;
    this.newException = false;
  }

  goToNewException(plate: string) {
    this.newException = true;
    this.newVehicle = false;
    this.selectedVehicle = plate;
  }

  changeFlag(flag: string) {
    // pedido quitar esto: 17/02/2025
    // switch (flag) {
    //   case 'email':
    //     this.applicant!.flags!.sms = this.applicant!.flags!.email ? false : this.applicant!.flags!.sms;
    //     break;
    //   case 'sms':
    //     this.applicant!.flags!.email = this.applicant!.flags!.sms ? false : this.applicant!.flags!.email;
    //     break;
    //   default:
    //     break;
    // }
  }

  resetCreate() {
    this.newVehicle = false;
  }

  // Track which fields have been touched
  touchedFields: { [key: string]: boolean } = {};
  formSubmitted = false;

  // Method to check if a field is valid
  isFieldValid(value: any): boolean {
    return value !== undefined && value !== null && value !== '';
  }

  // Mark a field as touched when user interacts with it
  markAsTouched(fieldName: string): void {
    this.touchedFields[fieldName] = true;
  }

  // Check if field should show error
  shouldShowError(fieldName: string, value: any): boolean {
    return (this.touchedFields[fieldName] || this.formSubmitted) && !this.isFieldValid(value);
  }
}
